/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState } from 'react';
import { Grid, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import {
  selectSummaryFilterList,
  selectPaceVisualFilterList,
  selectSummaryVisual,
  selectSummaryHotelVisual,
  selectSummaryHotelComparison,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import SummaryFilter from 'modules/dashboard/components/tab-container/summary-tab/components/filter-panel';
import { selectTabList } from 'modules/dashboard/selectors';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import useSummaryWidget from 'modules/dashboard/components/tab-container/summary-tab/hooks/use-summary-widget';
import { isEmpty } from '../../../../../../common/helpers/object';
import useSummaryVisualState from '../../hooks/use-summary-visual-state';
import useSummaryVisualStatus from '../../hooks/use-summary-visual-status';
import SummaryVisuals from './summary-visuals';

/**
 * Summary report with filters and embedded visuals
 * @param {Number} value - Summary tab value
 * @param {String} accessToken - Power BI access token
 * @param {Boolean} active - Whether the summary tab is active
 * @param {String} latestDate - Latest booking date of the selected hotel
 * @param {String} reportType - Report type of the active tab
 * @param {String} hotelTimezone - Timezone of the selected hotel
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @returns {JSX.Element} Summary tab content when active
 */
const SummaryWidget = ({
  value,
  accessToken,
  active,
  latestDate,
  reportType,
  hotelTimezone,
  toggleDisabled,
}) => {
  useSummaryWidget();
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const summaryFilterList = useSelector(selectSummaryFilterList);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const visuals = useSelector(selectSummaryVisual);
  const hotelVisuals = useSelector(selectSummaryHotelVisual);
  const isSummaryHotelComparison = useSelector(selectSummaryHotelComparison);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const revenue = useSummaryVisualState();
  const cancellations = useSummaryVisualState();
  const pace = useSummaryVisualState();
  const {
    isVisualLoading,
    visualToggleDisabled,
    setIsVisualCard1Loaded,
    setIsVisualCard2Loaded,
    setIsPaceVisualLoaded,
  } = useSummaryVisualStatus(latestDate, hotelTimezone);
  // To get visual details based on index
  const getVisual = (index) => {
    const found = isSummaryHotelComparison
      ? hotelVisuals?.find((visual) => visual?.order === index)
      : visuals?.find((visual) => visual?.order === index);
    return found;
  };
  // To get visual name based on index
  const getVisualName = (index) => getVisual(index)?.name;

  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    filter = summaryFilterList,
    setIsRendered = () => {},
    displayFunction = () => {}
  ) => {
    const found = getVisual(index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.SUMMARY}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  //
  return (
    active && (
      <Grid
        container
        direction="row"
        className={styles.mainGrid}
        sx={{
          display: value === TAB_VALUE.SUMMARY ? '' : 'none',
        }}
      >
        <Grid
          item
          xs={2}
          cc={2.5}
          md={2}
          sx={{
            ml: {
              xs: 0,
              cc: 0.25,
              md: 0,
            },
          }}
        >
          <SummaryFilter
            reportType={reportType}
            visual={getWidget(
              100,
              styles.symmetricComparisonWindow,
              () => {},
              () => {},
              summaryFilterList,
              () => {},
              summaryActions.setSymmetricComparisonIsDisplay
            )}
            visualName={getVisualName(100)}
            tabValue={value}
            toggleDisabled={toggleDisabled}
            latestDate={latestDate}
            hotelTimezone={hotelTimezone}
            isLoaded={isVisualLoading}
          />
        </Grid>
        {!isEmpty(summaryFilterList) && !isEmpty(paceVisualFilterList) && (
          <Grid item xs={10} cc={9.25} md={10}>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              <SummaryVisuals
                getWidget={getWidget}
                getVisualName={getVisualName}
                revenue={revenue}
                cancellations={cancellations}
                pace={pace}
                setIsVisualCard1Loaded={setIsVisualCard1Loaded}
                setIsVisualCard2Loaded={setIsVisualCard2Loaded}
                setIsPaceVisualLoaded={setIsPaceVisualLoaded}
                visualToggleDisabled={visualToggleDisabled}
                exportType={exportType}
                setExportType={setExportType}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    )
  );
};
//
export default SummaryWidget;
